import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Calendar, MapPin, Clock, Users, Loader2, Search, Filter, X, Trophy, Upload, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { trialAPI } from '../services/api';
import { toast } from 'sonner';
import { parseErrorMessage } from '../utils/errorHandler';

const Trials = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('open');
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);

  // Post Trial Modal states
  const [showPostTrialModal, setShowPostTrialModal] = useState(false);
  const [posting, setPosting] = useState(false);
  const [trialForm, setTrialForm] = useState({
    title: '',
    description: '',
    sport: '',
    location: '',
    date: '',
    time: '',
    ageGroup: 'Open',
    spotsAvailable: 20,
    requirements: '',
    imageUrl: '',
    applicationUrl: '',
    image: null
  });

  // Available filter options
  const sports = ['All Sports', 'Football', 'Basketball', 'Track & Field', 'Swimming', 'Tennis', 'Cricket', 'Rugby', 'Volleyball'];
  const locations = ['All Locations', 'Manchester', 'London', 'Birmingham', 'Liverpool', 'Leeds', 'Barcelona', 'Madrid', 'Los Angeles', 'New York'];
  const ageGroups = ['All Ages', 'U-12', 'U-14', 'U-16', 'U-18', 'U-21', 'Senior', 'Open'];
  const statuses = [
    { label: 'Open', value: 'open' },
    { label: 'Closed', value: 'closed' },
    { label: 'All', value: 'all' }
  ];

  const fetchTrials = async (pageNum = 1, resetList = false) => {
    try {
      setLoading(true);
      
      // Build filter parameters
      const sport = selectedSport !== 'all' ? selectedSport : null;
      const location = selectedLocation !== 'all' ? selectedLocation : null;
      const status = selectedStatus !== 'all' ? selectedStatus : 'open';
      
      const response = await trialAPI.getTrials(sport, location, status, pageNum, 12);
      const newTrials = response.data.trials;

      if (pageNum === 1 || resetList) {
        setTrials(newTrials);
      } else {
        setTrials((prev) => [...prev, ...newTrials]);
      }

      setHasMore(response.data.hasMore);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching trials:', error);
      toast.error('Failed to load trials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrials(1, true);
  }, [selectedSport, selectedLocation, selectedStatus]);

  // Check for create=true query param to open modal
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.persona === 'club') {
        setShowPostTrialModal(true);
        // Remove the query param after opening modal
        searchParams.delete('create');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  const handleLoadMore = () => {
    fetchTrials(page + 1);
  };

  // Filter trials based on search query and age group (client-side)
  const filteredTrials = trials.filter(trial => {
    const matchesSearch = searchQuery === '' || 
      trial.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trial.sport.toLowerCase().includes(searchQuery.toLowerCase()) ||
      trial.location.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAgeGroup = selectedAgeGroup === 'all' || trial.ageGroup === selectedAgeGroup;
    
    return matchesSearch && matchesAgeGroup;
  });

  const getDefaultImage = (sport) => {
    const images = {
      'Football': 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=600',
      'Soccer': 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=600',
      'Basketball': 'https://images.unsplash.com/photo-1546519638-68e109498ffc?w=600',
      'Track & Field': 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=600',
      'Athletics': 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=600',
      'Swimming': 'https://images.unsplash.com/photo-1519315901367-f34ff9154487?w=600',
      'Tennis': 'https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?w=600',
      'Cricket': 'https://images.unsplash.com/photo-1531415074968-036ba1b575da?w=600',
      'Rugby': 'https://images.unsplash.com/photo-1544298621-a21d5d0b0tried?w=600',
      'Golf': 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?w=600',
      'Boxing': 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=600',
      'MMA': 'https://images.unsplash.com/photo-1549719386-74dfcbf7dbed?w=600',
      'Volleyball': 'https://images.unsplash.com/photo-1612872087720-bb876e2e67d1?w=600',
      'Baseball': 'https://images.unsplash.com/photo-1508344928928-7165b0a59203?w=600',
      'Hockey': 'https://images.unsplash.com/photo-1515703407324-5f753afd8be8?w=600',
      'Gymnastics': 'https://images.unsplash.com/photo-1566241832378-917a0f30db2c?w=600',
      'Cycling': 'https://images.unsplash.com/photo-1517649763962-0c623066013b?w=600',
      'Badminton': 'https://images.unsplash.com/photo-1626224583764-f87db24ac4ea?w=600',
      'Table Tennis': 'https://images.unsplash.com/photo-1558657292-7db8fb50b33c?w=600',
      'Martial Arts': 'https://images.unsplash.com/photo-1555597673-b21d5c935865?w=600',
    };
    // Case-insensitive match
    const normalizedSport = sport?.trim();
    const matchedKey = Object.keys(images).find(
      key => key.toLowerCase() === normalizedSport?.toLowerCase()
    );
    return images[matchedKey] || 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=600';
  };

  const handlePostTrial = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!trialForm.title || !trialForm.description || !trialForm.sport || !trialForm.location || !trialForm.date || !trialForm.time) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setPosting(true);
    
    try {
      // Pass the form data as an object, the API will convert it to FormData
      await trialAPI.createTrial(trialForm);
      
      toast.success('Trial posted successfully!');
      setShowPostTrialModal(false);
      setTrialForm({
        title: '',
        description: '',
        sport: '',
        location: '',
        date: '',
        time: '',
        ageGroup: 'Open',
        spotsAvailable: 20,
        requirements: '',
        imageUrl: '',
        applicationUrl: '',
        image: null
      });
      
      // Refresh trial list
      fetchTrials(1, true);
      
    } catch (error) {
      console.error('Error posting trial:', error);
      const errorMessage = parseErrorMessage(error, 'Failed to post trial. Only clubs can post trials.');
      toast.error(errorMessage);
    } finally {
      setPosting(false);
    }
  };

  if (loading && page === 1) {
    return (
      <div className="min-h-screen bg-background pb-20 md:pb-0">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 pt-20" data-testid="trials-skeleton">
          <div className="py-4">
            <div className="text-center mb-4">
              <Skeleton className="h-6 w-40 mx-auto mb-2" />
              <Skeleton className="h-4 w-64 mx-auto" />
            </div>
            <Skeleton className="h-9 w-full rounded-xl mb-4" />
            {/* Mobile card skeletons */}
            <div className="md:hidden space-y-3">
              {[0, 1, 2, 3, 4].map((i) => (
                <Card key={i} className="overflow-hidden rounded-xl">
                  <CardContent className="p-3">
                    <div className="flex gap-3">
                      <Skeleton className="w-16 h-16 rounded-lg flex-shrink-0" />
                      <div className="flex-1 space-y-2">
                        <div className="flex justify-between">
                          <Skeleton className="h-3.5 w-32" />
                          <Skeleton className="h-5 w-16 rounded-full" />
                        </div>
                        <Skeleton className="h-3 w-40" />
                        <Skeleton className="h-3 w-28" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            {/* Desktop grid skeletons */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Card key={i} className="overflow-hidden rounded-2xl">
                  <Skeleton className="h-48 w-full rounded-none" />
                  <CardContent className="p-6 space-y-3">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-3.5 w-40" />
                    <Skeleton className="h-3.5 w-24" />
                    <Skeleton className="h-3.5 w-32" />
                    <Skeleton className="h-3.5 w-36" />
                    <Skeleton className="h-10 w-full rounded-md mt-2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 pt-20">
        <div className="py-4">
          {/* Header - Centered */}
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-foreground">Trials & Tryouts</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Find and register for upcoming sports trials</p>
          </div>

          {/* Search & Filter Row */}
          <div className="flex items-center gap-2 mb-4">
            {/* Search Bar */}
            <form onSubmit={(e) => e.preventDefault()} className="flex-1">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search trials..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 pr-8 h-9 text-sm rounded-xl"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => setSearchQuery('')}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors"
                  >
                    <X className="w-3.5 h-3.5 text-muted-foreground" />
                  </button>
                )}
              </div>
            </form>

            {/* Filter Button */}
            <Sheet open={filterSheetOpen} onOpenChange={setFilterSheetOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="h-9 px-2.5 rounded-xl md:hidden">
                  <Filter className="w-4 h-4" />
                  {(selectedSport !== 'all' || selectedLocation !== 'all' || selectedAgeGroup !== 'all' || selectedStatus !== 'open') && (
                    <span className="ml-1 w-4 h-4 rounded-full bg-primary text-white text-[10px] flex items-center justify-center">
                      {[selectedSport !== 'all', selectedLocation !== 'all', selectedAgeGroup !== 'all', selectedStatus !== 'open'].filter(Boolean).length}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent>
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                  <SheetDescription>
                    Filter trials by sport, location, age group, and status
                  </SheetDescription>
                </SheetHeader>
                <div className="space-y-6 mt-6">
                  {/* Sport Filter */}
                  <div>
                    <Label htmlFor="sport-mobile">Sport</Label>
                    <Select value={selectedSport} onValueChange={setSelectedSport}>
                      <SelectTrigger id="sport-mobile">
                        <SelectValue placeholder="Select sport" />
                      </SelectTrigger>
                      <SelectContent>
                        {sports.map((sport) => (
                          <SelectItem key={sport} value={sport === 'All Sports' ? 'all' : sport}>
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Location Filter */}
                  <div>
                    <Label htmlFor="location-mobile">Location</Label>
                    <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                      <SelectTrigger id="location-mobile">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((loc) => (
                          <SelectItem key={loc} value={loc === 'All Locations' ? 'all' : loc}>
                            {loc}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Age Group Filter */}
                  <div>
                    <Label htmlFor="ageGroup-mobile">Age Group</Label>
                    <Select value={selectedAgeGroup} onValueChange={setSelectedAgeGroup}>
                      <SelectTrigger id="ageGroup-mobile">
                        <SelectValue placeholder="Select age group" />
                      </SelectTrigger>
                      <SelectContent>
                        {ageGroups.map((age) => (
                          <SelectItem key={age} value={age === 'All Ages' ? 'all' : age}>
                            {age}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status Filter */}
                  <div>
                    <Label htmlFor="status-mobile">Status</Label>
                    <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                      <SelectTrigger id="status-mobile">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {statuses.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(selectedSport !== 'all' || selectedLocation !== 'all' || selectedAgeGroup !== 'all' || selectedStatus !== 'open') && (
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setSearchQuery('');
                        setSelectedSport('all');
                        setSelectedLocation('all');
                        setSelectedAgeGroup('all');
                        setSelectedStatus('open');
                      }}
                      className="w-full"
                    >
                      Clear All Filters
                    </Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop Filters */}
          <div className="hidden md:flex gap-4 mb-6 flex-wrap items-center">
            <Select value={selectedSport} onValueChange={setSelectedSport}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Sport" />
              </SelectTrigger>
              <SelectContent>
                {sports.map((sport) => (
                  <SelectItem key={sport} value={sport === 'All Sports' ? 'all' : sport}>
                    {sport}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedLocation} onValueChange={setSelectedLocation}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Location" />
              </SelectTrigger>
              <SelectContent>
                {locations.map((loc) => (
                  <SelectItem key={loc} value={loc === 'All Locations' ? 'all' : loc}>
                    {loc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedAgeGroup} onValueChange={setSelectedAgeGroup}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Age Group" />
              </SelectTrigger>
              <SelectContent>
                {ageGroups.map((age) => (
                  <SelectItem key={age} value={age === 'All Ages' ? 'all' : age}>
                    {age}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((status) => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(selectedSport !== 'all' || selectedLocation !== 'all' || selectedAgeGroup !== 'all' || selectedStatus !== 'open') && (
              <Button 
                variant="ghost" 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedSport('all');
                  setSelectedLocation('all');
                  setSelectedAgeGroup('all');
                  setSelectedStatus('open');
                }}
                className="text-sm"
              >
                <X className="w-4 h-4 mr-1" />
                Clear ({[selectedSport !== 'all', selectedLocation !== 'all', selectedAgeGroup !== 'all', selectedStatus !== 'open'].filter(Boolean).length})
              </Button>
            )}
          </div>

          {filteredTrials.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {searchQuery || selectedSport !== 'all' || selectedLocation !== 'all' || selectedAgeGroup !== 'all' || selectedStatus !== 'open'
                  ? 'No trials found matching your filters.'
                  : 'No trials available at the moment.'}
              </p>
            </div>
          ) : (
            <>
              {/* Mobile Compact Cards */}
              <div className="md:hidden space-y-3">
                {filteredTrials.map((trial) => (
                  <Card 
                    key={trial._id} 
                    className="overflow-hidden hover:shadow-md transition-all rounded-xl cursor-pointer border"
                    onClick={() => navigate(`/trials/${trial._id}`)}
                  >
                    <CardContent className="p-3">
                      <div className="flex gap-3">
                        {/* Left - Image/Logo */}
                        <div className="flex-shrink-0">
                          <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted">
                            <img
                              src={trial.imageUrl || getDefaultImage(trial.sport)}
                              alt={trial.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                e.target.src = getDefaultImage(trial.sport);
                              }}
                            />
                          </div>
                        </div>
                        
                        {/* Right - Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-foreground text-sm leading-tight line-clamp-1">
                              {trial.title}
                            </h3>
                            <Badge className="badge-primary text-[10px] px-2 py-0.5 flex-shrink-0">
                              {trial.sport}
                            </Badge>
                          </div>
                          
                          {/* Details */}
                          <div className="mt-1.5 space-y-0.5">
                            <div className="flex items-center text-xs text-muted-foreground">
                              <Calendar className="w-3 h-3 mr-1.5 text-primary" />
                              {new Date(trial.date).toLocaleDateString('en-US', { 
                                day: 'numeric',
                                month: 'short',
                                year: 'numeric'
                              })}
                              <span className="mx-1.5">•</span>
                              <Clock className="w-3 h-3 mr-1 text-primary" />
                              {trial.time}
                            </div>
                            <div className="flex items-center text-xs text-muted-foreground">
                              <MapPin className="w-3 h-3 mr-1.5 text-primary" />
                              <span className="truncate">{trial.location}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bottom - Attendees */}
                      <div className="flex items-center mt-3 pt-2 border-t border-border/50">
                        <div className="flex items-center">
                          {/* Stacked avatars */}
                          <div className="flex -space-x-2">
                            <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden">
                              <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100" alt="" className="w-full h-full object-cover" />
                            </div>
                            <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden">
                              <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100" alt="" className="w-full h-full object-cover" />
                            </div>
                            <div className="w-6 h-6 rounded-full bg-gray-300 border-2 border-white overflow-hidden">
                              <img src="https://images.unsplash.com/photo-1599566150163-29194dcabd36?w=100" alt="" className="w-full h-full object-cover" />
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground ml-2">
                            {trial.applicationsCount || trial.spotsAvailable || 0} {trial.applicationsCount !== undefined ? 'applied' : 'spots'}
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Desktop Grid Cards */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredTrials.map((trial) => (
                  <Card 
                    key={trial._id} 
                    className="overflow-hidden hover:shadow-lg transition-all rounded-2xl cursor-pointer"
                    onClick={() => navigate(`/trials/${trial._id}`)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={trial.imageUrl || getDefaultImage(trial.sport)}
                        alt={trial.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-4 right-4">
                        <Badge className="badge-primary">
                          {trial.sport}
                        </Badge>
                      </div>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-foreground mb-3">{trial.title}</h3>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-muted-foreground text-sm">
                          <Calendar className="w-4 h-4 mr-2 text-primary" />
                          {new Date(trial.date).toLocaleDateString('en-US', { 
                            weekday: 'short', 
                            month: 'short', 
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                        <div className="flex items-center text-muted-foreground text-sm">
                          <Clock className="w-4 h-4 mr-2 text-primary" />
                          {trial.time}
                        </div>
                        <div className="flex items-center text-muted-foreground text-sm">
                          <MapPin className="w-4 h-4 mr-2 text-primary" />
                          {trial.location}
                        </div>
                        <div className="flex items-center text-muted-foreground text-sm">
                          <Users className="w-4 h-4 mr-2 text-primary" />
                          {trial.spotsAvailable} spots • {trial.ageGroup}
                        </div>
                      </div>

                      <Button 
                        className="w-full btn-primary text-white"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/trials/${trial._id}`);
                        }}
                      >
                        View Details
                      </Button>

                      {/* Optional external link */}
                      {trial.applicationUrl && (
                        <a
                          href={trial.applicationUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="flex items-center justify-center text-xs text-primary hover:underline mt-2"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          External Registration Link
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {hasMore && (
                <div className="flex justify-center mt-8">
                  <Button
                    onClick={handleLoadMore}
                    disabled={loading}
                    variant="outline"
                    className="min-w-[200px]"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      'Load More'
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>


      {/* Post Trial Modal */}
      {showPostTrialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="bg-card rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl border-0">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border/50">
              <h2 className="text-lg font-semibold text-foreground">Post a Trial</h2>
              <button
                onClick={() => setShowPostTrialModal(false)}
                className="text-muted-foreground hover:text-foreground transition-colors rounded-full hover:bg-muted p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form Content */}
            <form onSubmit={handlePostTrial} className="p-5 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="space-y-4">
                {/* Trial Title */}
                <div>
                  <Label htmlFor="title">Trial Title *</Label>
                  <Input
                    id="title"
                    value={trialForm.title}
                    onChange={(e) => setTrialForm({...trialForm, title: e.target.value})}
                    placeholder="e.g., U18 Football Team Trials"
                    required
                  />
                </div>

                {/* Sport */}
                <div>
                  <Label htmlFor="sport">Sport *</Label>
                  <Select
                    value={trialForm.sport}
                    onValueChange={(value) => setTrialForm({...trialForm, sport: value})}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sport" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Football">Football</SelectItem>
                      <SelectItem value="Basketball">Basketball</SelectItem>
                      <SelectItem value="Tennis">Tennis</SelectItem>
                      <SelectItem value="Cricket">Cricket</SelectItem>
                      <SelectItem value="Rugby">Rugby</SelectItem>
                      <SelectItem value="Swimming">Swimming</SelectItem>
                      <SelectItem value="Track & Field">Track & Field</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Location */}
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    value={trialForm.location}
                    onChange={(e) => setTrialForm({...trialForm, location: e.target.value})}
                    placeholder="e.g., London, Manchester"
                    required
                  />
                </div>

                {/* Date and Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      value={trialForm.date}
                      onChange={(e) => setTrialForm({...trialForm, date: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="time">Time *</Label>
                    <Input
                      id="time"
                      type="time"
                      value={trialForm.time}
                      onChange={(e) => setTrialForm({...trialForm, time: e.target.value})}
                      required
                    />
                  </div>
                </div>

                {/* Age Group */}
                <div>
                  <Label htmlFor="ageGroup">Age Group</Label>
                  <Select
                    value={trialForm.ageGroup}
                    onValueChange={(value) => setTrialForm({...trialForm, ageGroup: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select age group" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">Open (All Ages)</SelectItem>
                      <SelectItem value="U12">Under 12</SelectItem>
                      <SelectItem value="U15">Under 15</SelectItem>
                      <SelectItem value="U18">Under 18</SelectItem>
                      <SelectItem value="U21">Under 21</SelectItem>
                      <SelectItem value="Senior">Senior (18+)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Spots Available */}
                <div>
                  <Label htmlFor="spotsAvailable">Spots Available</Label>
                  <Input
                    id="spotsAvailable"
                    type="number"
                    min="1"
                    value={trialForm.spotsAvailable}
                    onChange={(e) => setTrialForm({...trialForm, spotsAvailable: parseInt(e.target.value) || 20})}
                    placeholder="20"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <Label htmlFor="image">Trial Image (Optional)</Label>
                  <Input
                    id="image"
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setTrialForm({...trialForm, image: file});
                      }
                    }}
                    className="cursor-pointer"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Upload a trial poster or promotional image (JPG, PNG, max 5MB)
                  </p>
                  {trialForm.image && (
                    <div className="mt-2 text-sm text-primary">
                      Selected: {trialForm.image.name}
                    </div>
                  )}
                </div>

                {/* Application URL */}
                <div>
                  <Label htmlFor="applicationUrl">Registration Link (Optional)</Label>
                  <Input
                    id="applicationUrl"
                    type="url"
                    value={trialForm.applicationUrl}
                    onChange={(e) => setTrialForm({...trialForm, applicationUrl: e.target.value})}
                    placeholder="https://example.com/register or email@club.com"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Add an external registration link or email address
                  </p>
                </div>

                {/* Description */}
                <div>
                  <Label htmlFor="description">Trial Description *</Label>
                  <textarea
                    id="description"
                    value={trialForm.description}
                    onChange={(e) => setTrialForm({...trialForm, description: e.target.value})}
                    placeholder="Describe the trial, what to expect, and what athletes should bring..."
                    className="w-full min-h-[120px] px-3 py-2 text-sm rounded-xl border border-border bg-background"
                    required
                  />
                </div>

                {/* Requirements */}
                <div>
                  <Label htmlFor="requirements">Requirements (Optional)</Label>
                  <textarea
                    id="requirements"
                    value={trialForm.requirements}
                    onChange={(e) => setTrialForm({...trialForm, requirements: e.target.value})}
                    placeholder="List the skills, equipment, and prerequisites needed..."
                    className="w-full min-h-[100px] px-3 py-2 text-sm rounded-xl border border-border bg-background"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-border/50">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowPostTrialModal(false)}
                  disabled={posting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/80 text-white"
                  disabled={posting}
                >
                  {posting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Posting...
                    </>
                  ) : (
                    'Post Trial'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Trials;
